<script setup lang="ts">
import type { PerActionFieldRulesOrWildcard, PerActionItemRule, PolicyDocumentAction } from '#layers/access';
import type { BlockRootContext } from '#layers/block-engine';
import type { FormBlockSuccessEventPayload } from '~/composables/form-block/types';
import type { PolicyDrawerProps } from './PolicyCollectionDrawer.vue';
import { cloneDeep } from 'lodash-es';
import { computed, ref } from 'vue';
import { PageSlotProvider, TeleportPageActions } from '#layers/chrome';
import { useProjectSchemaStore } from '~/stores/project-schema';
import { isFieldRulesAction, isItemRulesAction } from '~/utils/policies';

interface PolicyCustomSettingsDrawerProps extends PolicyDrawerProps {
	action: PolicyDocumentAction;
}

const props = defineProps<PolicyCustomSettingsDrawerProps>();
const emit = defineEmits<{
	close: [payload?: FormBlockSuccessEventPayload];
}>();

const { findCollectionById } = useProjectSchemaStore();
const collection = computed(() => findCollectionById(props.collectionId));
const collectionName = computed<string>(() => collection.value?.apiName ?? '');

const blockContext = computed<BlockRootContext>(() => ({
	itemId: props.itemId,
	formMode: props.formMode,
	itemEdits: props.itemEdits,
	pageType: 'detail',
	pageIsFullWidth: false,
	pageDisplayMode: 'drawer',
}));

const itemRules = ref<PerActionItemRule>(cloneDeep(props.content.itemRules?.[collectionName.value]?.[props.action] ?? {}));
const fieldRules = ref<PerActionFieldRulesOrWildcard>(cloneDeep(props.content.fieldRules?.[collectionName.value]?.[props.action] ?? {}));

function save() {
	emit('close', {
		key: props.itemId!,
		data: {
			fieldRules: fieldRules.value,
			itemRules: itemRules.value,
		},
	});
}
</script>

<template>
	<DrawerPage
		:attach-to-route="false"
		:ui="{ container: 'gap-0 2xl:w-200', body: 'p-0' }"
		@close="() => emit('close')"
	>
		<template #header>
			<slot name="header">
				<h2 class="flex-1 text-lg font-semibold">
					<span class="flex-1 font-mono text-lg font-medium">{{ collectionName }}</span> → {{ $t(`policy.action.${action}`).toLowerCase() }}
				</h2>
			</slot>
		</template>
		<PageSlotProvider>
			<PageShell :context="blockContext">
				<div class="flex flex-col gap-8">
					<PolicyItemRules
						v-if="isItemRulesAction(action)"
						v-model="itemRules"
						:collection-id
						:action
					/>
					<PolicyFieldRules
						v-if="isFieldRulesAction(action)"
						v-model="fieldRules"
						:item-rules
						:collection-id
						:action
					/>
				</div>
			</PageShell>
			<TeleportPageActions>
				<DButton color="neutral" variant="outline" :label="$t('common.cancel')" @click="emit('close')" />
				<DButton color="primary" :label="$t('common.save')" @click="save" />
			</TeleportPageActions>
		</PageSlotProvider>
	</DrawerPage>
</template>
